import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Clock, TrendingUp, TrendingDown, AlertTriangle, Download, Calendar as CalIcon, User } from "lucide-react";

const monthToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
};

export default function TimesheetPage() {
  const [month, setMonth] = useState(monthToday());
  const [target, setTarget] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/timesheet?month=${month}&target_daily_hours=${target}`);
      setData(r.data);
      setSelectedEmp(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPdf = async (empId, name) => {
    try {
      const r = await api.get(`/timesheet/pdf?month=${month}&employee_id=${empId}&target_daily_hours=${target}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `timesheet_${name}_${month}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download PDF"); }
  };

  const totals = useMemo(() => {
    if (!data) return { hours: 0, target: 0, balance: 0, anomalies: 0 };
    return data.employees.reduce((acc, e) => ({
      hours: acc.hours + e.total_hours,
      target: acc.target + e.target_hours,
      balance: acc.balance + e.balance_hours,
      anomalies: acc.anomalies + e.anomalies_count,
    }), { hours: 0, target: 0, balance: 0, anomalies: 0 });
  }, [data]);

  return (
    <div className="p-4 md:p-12" data-testid="timesheet-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Timesheet · Ore lavorate</div>
        <h1 className="font-display text-4xl flex items-center gap-3"><Clock className="w-8 h-8 text-[#FF7A29]"/> Ore lavorate mensili</h1>
        <p className="text-[#555] mt-1 text-sm">Calcolo automatico ore da check-in/out · confronto con turni pianificati · export PDF</p>
      </div>

      <div className="card-smart p-6 mb-6 flex items-end flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label>Mese</Label>
          <Input type="month" data-testid="ts-month" value={month} onChange={e=>setMonth(e.target.value)} className="rounded-xl mt-1"/>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label>Ore target giornaliere</Label>
          <Input type="number" step="0.5" data-testid="ts-target" value={target} onChange={e=>setTarget(Number(e.target.value))} className="rounded-xl mt-1"/>
        </div>
        <Button onClick={load} disabled={loading} data-testid="ts-load" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-11">
          {loading ? "Calcolo…" : "Calcola timesheet"}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Dipendenti</div>
              <div className="font-display text-4xl mt-1">{data.employees.length}</div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Ore totali</div>
              <div className="font-display text-4xl mt-1">{totals.hours.toFixed(1)}h</div>
              <div className="text-xs text-[#757575]">target {totals.target.toFixed(0)}h</div>
            </div>
            <div className={`card-smart p-5 ${totals.balance >= 0 ? "bg-green-50" : "bg-red-50"}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Saldo globale</div>
              <div className={`font-display text-4xl mt-1 ${totals.balance >= 0 ? "text-green-700" : "text-red-700"}`}>
                {totals.balance >= 0 ? "+" : ""}{totals.balance.toFixed(1)}h
              </div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Anomalie</div>
              <div className="font-display text-4xl mt-1 text-amber-600">{totals.anomalies}</div>
            </div>
          </div>

          <div className="card-smart overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-[#EDE5D8] bg-[#FFFAF5]">
                <tr className="text-left">
                  <th className="p-4">Dipendente</th>
                  <th className="p-4">Ruolo</th>
                  <th className="p-4 text-right">Ore lavorate</th>
                  <th className="p-4 text-right">Ore target</th>
                  <th className="p-4 text-right">Saldo</th>
                  <th className="p-4 text-right">Anomalie</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {data.employees.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-[#757575]">Nessun dipendente trovato.</td></tr>}
                {data.employees.map((e) => (
                  <React.Fragment key={e.employee_id}>
                    <tr className="border-b border-[#EDE5D8] hover:bg-[#FFFAF5] cursor-pointer" onClick={()=>setSelectedEmp(selectedEmp === e.employee_id ? null : e.employee_id)} data-testid={`ts-row-${e.employee_id}`}>
                      <td className="p-4 font-semibold flex items-center gap-2"><User className="w-4 h-4 text-[#757575]"/>{e.name} {e.lastname}</td>
                      <td className="p-4 text-[#757575]">{e.job || "—"}</td>
                      <td className="p-4 text-right font-mono">{e.total_hours.toFixed(1)}h</td>
                      <td className="p-4 text-right font-mono text-[#757575]">{e.target_hours.toFixed(0)}h</td>
                      <td className={`p-4 text-right font-mono font-semibold ${e.balance_hours >= 0 ? "text-green-700" : "text-red-700"}`}>
                        {e.balance_hours >= 0 ? "+" : ""}{e.balance_hours.toFixed(1)}h
                      </td>
                      <td className="p-4 text-right">
                        {e.anomalies_count > 0 ? <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 w-fit ml-auto"><AlertTriangle className="w-3 h-3"/>{e.anomalies_count}</span> : <span className="text-[#757575] text-xs">—</span>}
                      </td>
                      <td className="p-4 text-right">
                        <Button size="sm" variant="outline" onClick={(ev)=>{ev.stopPropagation(); downloadPdf(e.employee_id, `${e.name}_${e.lastname}`);}} data-testid={`ts-pdf-${e.employee_id}`} className="rounded-full text-xs">
                          <Download className="w-3 h-3 mr-1"/> PDF
                        </Button>
                      </td>
                    </tr>
                    {selectedEmp === e.employee_id && (
                      <tr><td colSpan="7" className="bg-[#FFFAF5]/50 p-4">
                        <div className="grid grid-cols-7 gap-1 text-xs">
                          {e.days.map((d) => {
                            const day = new Date(d.date);
                            const isWeekend = [0,6].includes(day.getDay());
                            const hasAnomaly = !!d.anomaly;
                            const worked = d.hours > 0;
                            return (
                              <div key={d.date} className={`rounded-lg p-2 ${hasAnomaly ? "bg-red-50 border border-red-200" : worked ? "bg-green-50" : isWeekend ? "bg-gray-50" : "bg-white border border-[#EDE5D8]"}`}>
                                <div className="text-[10px] text-[#757575]">{day.getDate()}/{day.getMonth()+1}</div>
                                <div className="text-[10px] text-[#757575]">{["Dom","Lun","Mar","Mer","Gio","Ven","Sab"][day.getDay()]}</div>
                                {d.hours > 0 && <div className="font-mono font-bold text-green-700 mt-1">{d.hours.toFixed(1)}h</div>}
                                {d.in && <div className="text-[10px]">↓{d.in.slice(11,16)}</div>}
                                {d.out && <div className="text-[10px]">↑{d.out.slice(11,16)}</div>}
                                {d.anomaly && <div className="text-[10px] text-red-700 font-semibold mt-1">{d.anomaly}</div>}
                                {d.planned_hours && !worked && <div className="text-[10px] text-[#FF7A29]">pianif. {d.planned_hours}h</div>}
                              </div>
                            );
                          })}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
